import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FolderOpen,
  User,
  Calendar,
  Users,
  Target,
  ArrowRight,
  Edit
} from 'lucide-react';
import { Modal } from '@/components/ui';
import {
  GlassCard,
  GlassButton,
  GlassBadge,
} from '@/design-system';
import type { Program } from '@/types';

interface ProgramDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  program: Program | null;
  onEdit?: () => void;
}

const ProgramDetailModal: React.FC<ProgramDetailModalProps> = ({
  isOpen,
  onClose,
  program,
  onEdit
}) => {
  const navigate = useNavigate();

  if (!program) return null;

  const getLeadDisplayName = () => {
    if (program.leadType === 'USER' && program.lead) {
      return `${program.lead.firstName} ${program.lead.lastName}`;
    } else if (program.leadType === 'TEXT' && program.leadText) {
      return program.leadText;
    }
    return 'Unassigned';
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleViewTeams = () => {
    navigate(`/teams?programId=${program.id}`);
    onClose();
  };

  const handleViewEpics = () => {
    navigate(`/epics?programId=${program.id}`);
    onClose();
  };

  const teamsCount = program._count?.teams || 0;
  const epicsCount = program._count?.epics || 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      size="lg"
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30">
            <FolderOpen className="w-6 h-6 text-cyan-500" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-xl font-semibold text-foreground">
                {program.name}
              </h2>
              <GlassBadge variant={program.isActive ? 'success' : 'default'} size="sm">
                {program.isActive ? 'Active' : 'Inactive'}
              </GlassBadge>
            </div>
          </div>
        </div>

        {/* Description */}
        {program.description && (
          <GlassCard variant="subtle" padding="md">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {program.description}
            </p>
          </GlassCard>
        )}

        {/* Basic Information */}
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground/60 uppercase tracking-wide mb-3">
            Basic Information
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <GlassCard variant="subtle" padding="sm">
              <div className="flex items-center gap-2 mb-1">
                <User className="w-4 h-4 text-muted-foreground/60" />
                <span className="text-xs text-muted-foreground/60">Program Lead</span>
              </div>
              <p className="text-sm font-medium text-foreground">
                {getLeadDisplayName()}
              </p>
            </GlassCard>

            <GlassCard variant="subtle" padding="sm">
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="w-4 h-4 text-muted-foreground/60" />
                <span className="text-xs text-muted-foreground/60">Created</span>
              </div>
              <p className="text-sm font-medium text-foreground">
                {formatDate(program.createdAt)}
              </p>
            </GlassCard>

            <GlassCard variant="subtle" padding="sm">
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="w-4 h-4 text-muted-foreground/60" />
                <span className="text-xs text-muted-foreground/60">Last Updated</span>
              </div>
              <p className="text-sm font-medium text-foreground">
                {formatDate(program.updatedAt)}
              </p>
            </GlassCard>
          </div>
        </div>

        {/* Quick Stats */}
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground/60 uppercase tracking-wide mb-3">
            Quick Stats
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <GlassCard variant="subtle" padding="sm" className="text-center">
              <div className="flex items-center justify-center mb-2">
                <Users className="w-4 h-4 text-accent-cyan" />
              </div>
              <div className="text-2xl font-bold text-accent-cyan">
                {teamsCount}
              </div>
              <div className="text-xs text-muted-foreground/60">Teams</div>
            </GlassCard>

            <GlassCard variant="subtle" padding="sm" className="text-center">
              <div className="flex items-center justify-center mb-2">
                <Target className="w-4 h-4 text-accent-violet" />
              </div>
              <div className="text-2xl font-bold text-accent-violet">
                {epicsCount}
              </div>
              <div className="text-xs text-muted-foreground/60">EPICs</div>
            </GlassCard>
          </div>
        </div>

        {/* Related Teams */}
        <GlassCard variant="subtle" padding="md">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-accent-cyan/10">
                <Users className="w-5 h-5 text-accent-cyan" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  Delivery Teams
                </h3>
                <p className="text-sm text-muted-foreground/60">
                  {teamsCount} team{teamsCount !== 1 ? 's' : ''} in this program
                </p>
              </div>
            </div>
            {teamsCount > 0 && (
              <GlassButton
                variant="secondary"
                size="sm"
                onClick={handleViewTeams}
                rightIcon={<ArrowRight className="w-4 h-4" />}
              >
                View Teams
              </GlassButton>
            )}
          </div>
        </GlassCard>

        {/* Related EPICs */}
        <GlassCard variant="subtle" padding="md">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-accent-violet/10">
                <Target className="w-5 h-5 text-accent-violet" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  EPICs
                </h3>
                <p className="text-sm text-muted-foreground/60">
                  {epicsCount} EPIC{epicsCount !== 1 ? 's' : ''} owned by this program
                </p>
              </div>
            </div>
            {epicsCount > 0 && (
              <GlassButton
                variant="secondary"
                size="sm"
                onClick={handleViewEpics}
                rightIcon={<ArrowRight className="w-4 h-4" />}
              >
                View EPICs
              </GlassButton>
            )}
          </div>
        </GlassCard>

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
          <GlassButton variant="ghost" onClick={onClose}>
            Close
          </GlassButton>
          {onEdit && (
            <GlassButton
              variant="primary"
              leftIcon={<Edit className="w-4 h-4" />}
              onClick={onEdit}
            >
              Edit Program
            </GlassButton>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ProgramDetailModal;
